import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { VoucherRequiredDialogService } from '@/components';
import { ErrorService } from '@/services';
import { VoucherService } from 'src/app/services/voucher/voucher.service';

/**
 * Route guard factory for routes gated by a per-user product voucher. Usage:
 *   { path: 'thing', canActivate: [voucherGuard('NNN')], ... }
 *
 * Deliberately does NOT also check FeatureFlagsService here: that cache is populated once at
 * login and never refreshed mid-session, so it can silently drift from the server (which is what
 * actually decides whether the function is shown at all, via CategoryService). A stale client
 * cache previously caused this guard to redirect home even when the function was legitimately
 * visible and the user just lacked a voucher. The voucher check below always hits the backend
 * fresh, and a tenant with the flag truly off never renders the function in the first place.
 *
 * No active voucher -> cancel the navigation and open the "voucher required" dialog (same one
 * shown from the dashboard/sidebar) with a button to /account/vouchers. This is UX only: the
 * backend enforces the same rule on every request regardless of whether this guard runs.
 *
 * The HTTP call is wrapped in catchError: an rxjs `map` never runs its callback when its source
 * errors, so without this a failed request (network blip, 401 mid-refresh, backend error) would
 * silently cancel the navigation without ever opening the dialog or surfacing anything to the
 * user or to Sentry — exactly the "just goes back to the dashboard, nothing shown" failure mode.
 */
export const voucherGuard =
  (productType: string): CanActivateFn =>
  () => {
    const voucherService = inject(VoucherService);
    const voucherRequiredDialogService = inject(VoucherRequiredDialogService);
    const errorService = inject(ErrorService);

    return voucherService.hasActiveVoucher(productType).pipe(
      map(hasVoucher => {
        if (hasVoucher) {
          return true;
        }
        voucherRequiredDialogService.open();
        return false;
      }),
      catchError(err => {
        errorService.onError(err);
        return of(false);
      })
    );
  };
